import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useState, useEffect } from "react";
import {
  Target,
  Shield,
  Award,
  TrendingUp,
  Users,
  Globe,
  CheckCircle,
  Linkedin,
  Mail,
  ChevronLeft,
  ChevronRight,
  Quote,
  Briefcase,
  Star,
  Clock,
  Building,
  Rocket,
} from "lucide-react";

const Slide3Leadership = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  const [activeLeader, setActiveLeader] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const leaders = [
    {
      id: 1,
      name: "Mubashir Ashraf",
      title: "CEO & Founder",
      role: "Chief Executive Officer",
      tagline: "Visionary Architect",
      headline: "The Future Is Built in Silence",
      content: `In the competitive tech landscape, we focus on substance over hype. QUAVRION was founded on a simple principle: real innovation speaks through results, not marketing. We build systems that last, not just trends that fade.`,
      quote: "Build systems that outlast the noise.",
      icon: Target,
      color: "cyan",
      stats: [
        { label: "Industry", value: "12+", icon: Clock, suffix: "years" },
        { label: "Companies", value: "3", icon: Building, suffix: "founded" },
        { label: "Markets", value: "8+", icon: Globe, suffix: "countries" },
      ],
      achievements: [
        "AI Innovation Award 2023",
        "Fortune 500 Partnerships",
        "Quantum Computing Pioneer",
        "Tech Leadership Recognition",
      ],
      social: {
        linkedin: "#",
        email: "ceo@quavrion.com",
      },
    },
    {
      id: 2,
      name: "Sajeed Ansari",
      title: "CTO & Co-Founder",
      role: "Chief Technology Officer",
      tagline: "Architect of Stability",
      headline: "Engineering Excellence",
      content: `Technology should work so perfectly that it becomes invisible. My focus is on building robust, scalable systems that form the foundation for innovation. We engineer solutions that grow with your business.`,
      quote: "The best systems are the ones you never notice.",
      icon: Shield,
      color: "blue",
      stats: [
        { label: "Systems", value: "200+", icon: Rocket, suffix: "built" },
        { label: "Uptime", value: "99.99", icon: TrendingUp, suffix: "%" },
        { label: "Team", value: "50+", icon: Users, suffix: "engineers" },
      ],
      achievements: [
        "Cloud Infrastructure Expert",
        "Security Protocol Innovator",
        "System Architecture Awards",
        "Open Source Contributor",
      ],
      social: {
        linkedin: "#",
        email: "cto@quavrion.com",
      },
    },
  ];

  const companyStats = [
    {
      icon: TrendingUp,
      label: "Growth",
      value: "300%",
      color: "text-green-400",
    },
    { icon: Users, label: "Clients", value: "85+", color: "text-blue-400" },
    { icon: Globe, label: "Global", value: "12", color: "text-cyan-400" },
    { icon: Award, label: "Awards", value: "25", color: "text-yellow-400" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <div
      id="leadership"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden bg-black py-8 md:py-12"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/20 to-black"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="max-w-7xl mx-auto"
        >
          {/* Header - Mobile Optimized */}
          <motion.div
            variants={itemVariants}
            className="text-center mb-8 md:mb-16 px-2"
          >
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
              <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></div>
              <span className="text-cyan-300 text-xs md:text-sm font-medium">
                LEADERSHIP TEAM
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-6">
              <span className="block text-white mb-1 md:mb-2">Meet Our</span>
              <span className="block bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Leadership
              </span>
            </h2>

            <p className="text-sm sm:text-base md:text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed px-2">
              Visionary leadership driving digital transformation across
              industries
            </p>
          </motion.div>

          {/* Mobile Navigation Dots */}
          {isMobile && (
            <div className="flex justify-center mb-6">
              <div className="flex gap-2">
                {leaders.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveLeader(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      activeLeader === index ? "w-6 bg-cyan-500" : "bg-gray-700"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Leadership Cards - Responsive Layout */}
          <div className="relative">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              {/* Left Column - Leader Profile */}
              <div className="lg:w-1/2">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeLeader}
                    initial={{ opacity: 0, x: isMobile ? 0 : -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: isMobile ? 0 : 20 }}
                    transition={{ duration: 0.3 }}
                    className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl md:rounded-3xl border border-gray-800 p-4 sm:p-6 md:p-8"
                  >
                    {/* Leader Header */}
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <div
                            className={`p-2 rounded-lg ${
                              leaders[activeLeader].color === "cyan"
                                ? "bg-cyan-500/10"
                                : "bg-blue-500/10"
                            }`}
                          >
                            {/* <leaders[activeLeader].icon className={`w-5 h-5 ${
                              leaders[activeLeader].color === 'cyan' 
                                ? 'text-cyan-400' 
                                : 'text-blue-400'
                            }`} /> */}
                          </div>
                          <span className="text-xs text-gray-400 font-medium">
                            LEADERSHIP
                          </span>
                        </div>
                        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1">
                          {leaders[activeLeader].name}
                        </h3>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                          <span className="text-sm sm:text-base text-gray-300">
                            {leaders[activeLeader].title}
                          </span>
                          <span className="hidden sm:block text-gray-600">
                            •
                          </span>
                          <span
                            className={`text-sm font-medium ${
                              leaders[activeLeader].color === "cyan"
                                ? "text-cyan-400"
                                : "text-blue-400"
                            }`}
                          >
                            {leaders[activeLeader].tagline}
                          </span>
                        </div>
                      </div>

                      {/* Profile Image - Hidden on mobile, shown on tablet+ */}
                      <div className="hidden sm:block relative">
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-gradient-to-br from-white/10 to-transparent border border-cyan-400/30 flex items-center justify-center">
                          <span className="text-2xl md:text-3xl font-bold text-cyan-400">
                            {leaders[activeLeader].name.charAt(0)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Headline */}
                    <h4 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 md:mb-6">
                      <span
                        className={
                          leaders[activeLeader].color === "cyan"
                            ? "bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent"
                            : "bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent"
                        }
                      >
                        {leaders[activeLeader].headline}
                      </span>
                    </h4>

                    {/* Content */}
                    <div className="mb-6 md:mb-8">
                      <div className="relative">
                        <Quote className="absolute -top-2 -left-2 w-5 h-5 text-cyan-400/20" />
                        <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                          {leaders[activeLeader].content}
                        </p>
                      </div>

                      {/* Quote */}
                      <div className="mt-4 p-3 sm:p-4 border-l-2 sm:border-l-3 border-cyan-400 bg-cyan-400/5 rounded-r-lg">
                        <p className="text-xs sm:text-sm text-cyan-300 italic">
                          "{leaders[activeLeader].quote}"
                        </p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-3 mb-6">
                      {leaders[activeLeader].stats.map((stat, index) => (
                        <div
                          key={index}
                          className="p-3 rounded-xl border border-gray-800 bg-gray-900/50 text-center"
                        >
                          <div className="flex justify-center mb-1">
                            <stat.icon
                              className={`w-4 h-4 ${
                                leaders[activeLeader].color === "cyan"
                                  ? "text-cyan-400"
                                  : "text-blue-400"
                              }`}
                            />
                          </div>
                          <div className="text-lg sm:text-xl font-bold text-white">
                            {stat.value}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {stat.label}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Social Links */}
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <a
                          href={leaders[activeLeader].social.linkedin}
                          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                        >
                          <Linkedin className="w-4 h-4 text-cyan-400" />
                        </a>
                        <a
                          href={`mailto:${leaders[activeLeader].social.email}`}
                          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                        >
                          <Mail className="w-4 h-4 text-gray-400" />
                        </a>
                      </div>

                      {/* Role Badge */}
                      <div
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          leaders[activeLeader].color === "cyan"
                            ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20"
                            : "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                        }`}
                      >
                        {leaders[activeLeader].role}
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Right Column - Achievements & Stats */}
              <div className="lg:w-1/2 space-y-6 md:space-y-8">
                {/* Achievements Card */}
                <motion.div
                  variants={itemVariants}
                  className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl md:rounded-3xl border border-gray-800 p-4 sm:p-6 md:p-8"
                >
                  <h4 className="text-lg sm:text-xl font-bold text-white mb-4 md:mb-6 flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-400" />
                    Key Achievements
                  </h4>
                  <div className="space-y-3">
                    {leaders[activeLeader].achievements.map(
                      (achievement, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-start gap-3"
                        >
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm sm:text-base text-gray-300">
                            {achievement}
                          </span>
                        </motion.div>
                      )
                    )}
                  </div>
                </motion.div>

                {/* Company Stats Card */}
                <motion.div
                  variants={itemVariants}
                  className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl md:rounded-3xl border border-gray-800 p-4 sm:p-6 md:p-8"
                >
                  <h4 className="text-lg sm:text-xl font-bold text-white mb-4 md:mb-6 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    Company Performance
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                    {companyStats.map((stat, index) => (
                      <div
                        key={index}
                        className="p-3 sm:p-4 rounded-xl border border-gray-800 bg-gray-900/50 text-center"
                      >
                        <div className="flex justify-center mb-2">
                          <stat.icon
                            className={`w-4 h-4 sm:w-5 sm:h-5 ${stat.color}`}
                          />
                        </div>
                        <div className="text-lg sm:text-xl font-bold text-white mb-1">
                          {stat.value}
                        </div>
                        <div className="text-xs text-gray-400">
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Leadership Navigation (Desktop) */}
                {!isMobile && (
                  <motion.div
                    variants={itemVariants}
                    className="flex items-center justify-between"
                  >
                    <button
                      onClick={() =>
                        setActiveLeader(
                          (prev) => (prev - 1 + leaders.length) % leaders.length
                        )
                      }
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900/50 hover:bg-gray-800/50 transition-colors border border-gray-800"
                    >
                      <ChevronLeft className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-300">Previous</span>
                    </button>

                    <div className="flex gap-2">
                      {leaders.map((leader, index) => (
                        <button
                          key={leader.id}
                          onClick={() => setActiveLeader(index)}
                          className={`px-4 py-2 rounded-lg transition-all ${
                            activeLeader === index
                              ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                              : "bg-gray-900/30 text-gray-400 hover:text-gray-300 border border-gray-800"
                          }`}
                        >
                          <span className="text-sm font-medium">
                            {leader.name.split(" ")[0]}
                          </span>
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() =>
                        setActiveLeader((prev) => (prev + 1) % leaders.length)
                      }
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900/50 hover:bg-gray-800/50 transition-colors border border-gray-800"
                    >
                      <span className="text-sm text-gray-300">Next</span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </button>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Mobile Navigation Buttons */}
            {isMobile && (
              <motion.div
                variants={itemVariants}
                className="flex justify-between mt-6"
              >
                <button
                  onClick={() =>
                    setActiveLeader(
                      (prev) => (prev - 1 + leaders.length) % leaders.length
                    )
                  }
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900/50 hover:bg-gray-800/50 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">Previous</span>
                </button>

                <button
                  onClick={() =>
                    setActiveLeader((prev) => (prev + 1) % leaders.length)
                  }
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900/50 hover:bg-gray-800/50 transition-colors"
                >
                  <span className="text-sm text-gray-300">Next</span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              </motion.div>
            )}
          </div>

          {/* Closing Statement */}
          <motion.div variants={itemVariants} className="mt-8 md:mt-16">
            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-2xl md:rounded-3xl border border-gray-800 p-6 md:p-8 text-center">
              <div className="text-cyan-400 text-lg md:text-xl font-bold mb-4">
                VISION × EXECUTION = IMPACT
              </div>

              <p className="text-sm md:text-base text-gray-300 max-w-2xl mx-auto leading-relaxed">
                Strategic vision combined with technical excellence creates
                transformative results. Our leadership brings together
                innovation and execution to deliver solutions that matter.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 mt-4">
                <div className="flex items-center gap-1 md:gap-2">
                  <Target className="w-3 h-3 md:w-4 md:h-4 text-cyan-400" />
                  <span className="text-xs md:text-sm text-gray-400">
                    Vision
                  </span>
                </div>
                <span className="text-gray-600">×</span>
                <div className="flex items-center gap-1 md:gap-2">
                  <Shield className="w-3 h-3 md:w-4 md:h-4 text-blue-400" />
                  <span className="text-xs md:text-sm text-gray-400">
                    Execution
                  </span>
                </div>
                <span className="text-gray-600">=</span>
                <div className="flex items-center gap-1 md:gap-2">
                  <Star className="w-3 h-3 md:w-4 md:h-4 text-green-400" />
                  <span className="text-xs md:text-sm text-gray-400">
                    Results
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Slide3Leadership;
